/**
 * NovelMello 品牌支付配置
 * 结构参考 storlyn.ts 的字段形状，取值全部独立（仅主色与资源路径），视觉细节由各页面计划继续细化。
 */
import type { BrandPaymentConfig } from '../types'
import { chapterCommonTexts, payCommonTexts, subscribeCommonTexts } from '../common'
import { colors as brandColors, theme as brandTheme } from '@/config/brands'

function hexToRgba(color: string, alpha: number) {
  if (!color.startsWith('#'))
    return color

  const hex = color.slice(1)
  const normalizedHex = hex.length === 3
    ? hex.split('').map(char => char + char).join('')
    : hex

  const red = Number.parseInt(normalizedHex.slice(0, 2), 16)
  const green = Number.parseInt(normalizedHex.slice(2, 4), 16)
  const blue = Number.parseInt(normalizedHex.slice(4, 6), 16)

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

const primaryGradientStart = '#F0509B'
const primaryGradientEnd = '#9B5CF0'
const primaryGradient = `linear-gradient(90deg, ${primaryGradientStart} 0%, ${primaryGradientEnd} 100%)`
const paymentStickyBgStart = brandTheme.bgGlobal || '#FBF6FB'
const paymentStickyGradient = `linear-gradient(to top, ${paymentStickyBgStart} 0%, ${paymentStickyBgStart} 34%, ${hexToRgba(paymentStickyBgStart, 0.92)} 58%, ${hexToRgba(paymentStickyBgStart, 0.72)} 74%, ${hexToRgba(paymentStickyBgStart, 0.36)} 88%, ${hexToRgba(paymentStickyBgStart, 0)} 100%)`

export const novelmelloPaymentConfig: BrandPaymentConfig = {
  brand: 'novelmello',

  pay: {
    theme: {
      isLightPaymentSelector: true,
      paymentStickyGradient,
      colors: {
        pageBg: '#FBF6FB',
        cardBg: '#FFFFFF',
        cardBorder: '#F2EAF4',
        cardActiveBorder: '#F0509B',
        cardActiveShadow: 'rgba(240, 80, 155, 0.16)',
        cardRightBg: '#FDEFF6',
        bonusPillBg: '#FDEFF6',
        bonusPillText: '#F0509B',
        bonusRateText: '#F0509B',
        bottomBarBg: 'rgba(255, 255, 255, 0.98)',
        bottomBarBorder: '#F2EAF4',
        totalHighlight: '#F0509B',
        payBtnGradient: primaryGradient,
        payBtnShadow: 'rgba(124, 77, 232, 0.22)',
        tagGradient: 'linear-gradient(90deg, #F0509B 0%, #FFB23E 100%)',
      },
    },
    texts: payCommonTexts,
    resources: {
      goldCoinIcon: '/images/novelmello/welfare-gold.png',
    },
  },

  subscribe: {
    texts: subscribeCommonTexts,
    darkPaymentSelector: false,
    resources: {
      vipBg: '/images/novelmello/vip_bg.webp',
      memberSubscribeBg: '/images/novelmello/member_subscribe_bg.webp',
      vipIcon: '/images/novelmello/vip_icon2.png',
      vipDiscountIcon: '/images/novelmello/vip_discount.png',
      discountBg: '/images/novelmello/discount.avif',
      showVipIcon: true,
    },
    colors: {
      redGradient: primaryGradient,
      purpleTextGradient: 'linear-gradient(180deg, #F0509B 0%, #9B5CF0 100%)',
      subscribeText1: brandColors!.primary,
      subscribeText2: brandColors!.text.primary,
      subscribeText3: brandColors!.text.secondary,
      subscribeText4: brandColors!.text.secondary,
      subscribeCardBg: 'linear-gradient(90deg, #FFFFFF 0%, #FDEFF6 100%)',
      cycleTextGradient: 'linear-gradient(180deg, #F0509B 0%, #9B5CF0 100%)',
      badgeGradient: 'linear-gradient(132deg, #F0509B 0%, #9B5CF0 100%)',
      pageBg: '#FBF6FB',
      paymentCardBg: '#FFFFFF',
      paymentCardBorder: '#F2EAF4',
      paymentCardActiveBorder: '#F0509B',
      tagGradient: 'linear-gradient(90deg, #F0509B 0%, #FFB23E 100%)',
      titleColor: '#2B1B34',
      schemes: {
        discounted: {
          bg: 'linear-gradient(270deg, #FDE0EF 0%, #FBD3E6 100%)',
          priceBg: 'linear-gradient(90deg, #F0509B 0%, #9B5CF0 100%)',
          titleColor: '#2B1B34',
          descColor: '#71607E',
          borderColor: 'transparent',
          unitPriceColor: '#71607E',
          originalPriceColor: '#71607E',
          cycleLabelBg: 'linear-gradient(90deg, #FDE0EF 0%, #FBD3E6 100%)',
          cycleLabelColor: '#2B1B34',
        },
        normal: {
          bg: 'linear-gradient(90deg, #F3EDFE 0%, #EDE4FD 100%)',
          priceBg: 'linear-gradient(90deg, #9B5CF0 0%, #7C4DE8 100%)',
          titleColor: '#2B1B34',
          descColor: '#71607E',
          borderColor: 'transparent',
          unitPriceColor: '#71607E',
          originalPriceColor: '#71607E',
          cycleLabelBg: 'linear-gradient(270deg, #F3EDFE 0%, #EDE4FD 100%)',
          cycleLabelColor: '#2B1B34',
        },
        fakeDiscount: {
          bg: 'linear-gradient(270deg, #FFF1DE 0%, #FFE4BC 100%)',
          priceBg: 'linear-gradient(90deg, #FFB23E 0%, #F0509B 100%)',
          titleColor: '#2B1B34',
          descColor: '#8A6A3A',
          borderColor: 'transparent',
          unitPriceColor: '#8A6A3A',
          originalPriceColor: '#8A6A3A',
          cycleLabelBg: 'linear-gradient(270deg, #FFE9CC 0%, #FFDFB0 100%)',
          cycleLabelColor: '#775217',
        },
      },
    },
  },

  chapter: {
    texts: chapterCommonTexts,
    darkPaymentSelector: false,
    themeDefaults: {
      background: '#FFFCFA',
      size: '38',
    },
    colors: {
      coinsGradient: `linear-gradient(180deg, ${brandColors!.primary}, ${brandColors!.bg.global})`,
      coinsPopupText1: brandColors!.text.inverse,
      coinsPopupText2: primaryGradientStart,
      coinsPopupText3: brandColors!.text.secondary,
      coinsPopupText4: brandColors!.text.inverse,
      vipBg: brandColors!.bg.card,
      subscribeActiveBg: brandColors!.bg.card,
      purpleTextGradient: 'linear-gradient(180deg, #F0509B 0%, #9B5CF0 100%)',
      pinkGradient: primaryGradient,
      subscribeCardBg: 'linear-gradient(90deg, #FFFFFF 0%, #FDEFF6 100%)',
      purchaseActiveBg: '#FDEFF6',
      memberSubscribeBg: '/images/novelmello/chapter/member_subscribe_bg.webp',
      showVipIcon: true,
    },
  },
}
